import _ from 'lodashParts'
import TextEngineAdapter from './../ckUtils/ckBridge'
import {fixLinks, transformLinkRefsToAttributes, transformAttributesToLinkRef, extractLinksRefs} from './../utils/linkUtils'
//import {commands, commandsDependentOnTheme} from './../utils/cmdUtils'
import * as propsCollectorUtils from './../propsCollector/utils/propsCollectorUtils'
import mainControllerEvents from './mainControllerEvents'
import mainControllerCommands from './mainControllerCommands'
import constants from '../utils/constants'
import richTextUtils from 'santa-platform-utils/src/richTextUtils'

const FONT_URL_ID_PREFIX = 'wix_richtext_font_url_'
const CLONED_FONT_URL_ID_PREFIX = 'cloned_'
const CLONED_FONT_URL_ID = FONT_URL_ID_PREFIX + CLONED_FONT_URL_ID_PREFIX
const engineAdapterFunctionToExpose = [
    'canRedo',
    'canUndo',
    'collapseSelection',
    'focus',
    'hasSelection',
    'isMultipleBlockSelection',
    'resetUndo',
    'resize',
    'setReadOnly',
    'undo',
    'redo'
]

const applyTransformationForWixCodeSetHtml = htmlStr => {
    const div = document.createElement('div')
    div.innerHTML = htmlStr
    htmlStr = div.innerHTML
    htmlStr = richTextUtils.applyTransformationForSetHtml(htmlStr)
    return htmlStr
}

const injectInitialCssToCk = (cssData, getFontUrlsCss, mainControler, engineAdapter, shouldInjectFontsToMainDocument) => {
    _.forEach(cssData, cssValue => mainControler.execCommand('wixCss', cssValue))

    _.forEach(getFontUrlsCss, cssValue => {
        cssValue.id = FONT_URL_ID_PREFIX + cssValue.id
        mainControler.execCommand('wixCss', cssValue)
    })

    if (shouldInjectFontsToMainDocument) {
        _.forEach(document.querySelectorAll(`link[id^="${CLONED_FONT_URL_ID}"]`), el => {
            document.head.removeChild(el)
        })

        const editorDOMElement = engineAdapter.getEditorDOMElement() || {querySelectorAll: _.noop}

        _.forEach(editorDOMElement.querySelectorAll(`link[id^="${FONT_URL_ID_PREFIX}"]`), el => {
            const clone = el.cloneNode(true)
            clone.id = CLONED_FONT_URL_ID_PREFIX + clone.id
            document.head.appendChild(clone)
        })
    }
}

function initExposeEngineAdapterFunctions(engineAdapter) {
    engineAdapterFunctionToExpose.forEach(funcName => {
        this[funcName] = (...args) => engineAdapter[funcName](...args)
    })
}

function initMainControllerPublicMethods(privates) {
    const {
        propsCollector,
        engineAdapter,
        shouldInjectFontsToMainDocument
        } = privates

    this.getCommandsStates = () => privates.lastCommandStates

    this.getData = () => transformLinkRefsToAttributes(engineAdapter.getData(), propsCollector.convertLinkRefToAttributes)

    this.getRawData = () => engineAdapter.getData()

    this.setData = (data, {setSelectionOnStart = true, validateHtml = true, cursorLocation = constants.CURSOR_LOCATIONS.START, callback = _.noop} = {}) => {
        data = transformAttributesToLinkRef(data, propsCollector.convertAttributesToLinkRef)
        if (validateHtml) {
            data = applyTransformationForWixCodeSetHtml(data)
        }
        engineAdapter.setData(data, {setSelectionOnStart, validateHtml, cursorLocation, callback})
        privates.calculateCommandStates()
    }

    this.getLinkRefsList = () => extractLinksRefs(engineAdapter.getData())

    this.unRegisterListener = listener => {
        engineAdapter.removeListener(listener)
        privates.unRegisterListener(listener)
    }

    this.destroy = () => {
        privates.unRegisterAllListeners()
        engineAdapter.destroy()
    }

    this.getFonts = getExtendedObj => propsCollector.getNormalizedFonts(getExtendedObj)

    this.getThemes = () => propsCollector.getCachedThemes()

    this.getAvailableTextShadowEffects = () => constants.EFFECTS

    this.refreshPropsCollector = () => {
        propsCollectorUtils.buildPropsCollectorCalculatedMethods(propsCollector)
        injectInitialCssToCk(propsCollector.getCssData(), propsCollector.getFontUrlsCss(), this, engineAdapter, shouldInjectFontsToMainDocument)
    }

    this.getDimensions = () => engineAdapter.getDimensions()

    this.scrollToTop = () => engineAdapter.scrollToTop()

    this.isWixRichtextHtmlCompatible = htmlStr => {
        htmlStr = applyTransformationForWixCodeSetHtml(htmlStr)
        return engineAdapter.isValidHTML(htmlStr)
    }

    if (window.isRunningTests) {
        this.setDataWithSelection = data => {
            data = transformAttributesToLinkRef(data, propsCollector.convertAttributesToLinkRef)

            return engineAdapter.setDataWithSelection(data)
        }
    }
}

function toggleKeyStrokesIfNeeded(themeStyles, engineAdapter) {
    if (themeStyles.bold) {
        engineAdapter.setKeystroke(constants.KEYS.CTRL + 66, 'unBold') //enable Ctrl + B assignment to unBold command
    } else {
        engineAdapter.setKeystroke(constants.KEYS.CTRL + 66, 'bold') //enable Ctrl + B assignment to Bold command
    }

    if (themeStyles.italic) {
        engineAdapter.setKeystroke(constants.KEYS.CTRL + 73, 'unItalic') //enable Ctrl + I assignment to unItalic command
    } else {
        engineAdapter.setKeystroke(constants.KEYS.CTRL + 73, 'italic') //enable Ctrl + I assignment to Italic command
    }
}

function initMainControllerPrivateMethods(privates) {
    const {engineAdapter, propsCollector} = privates

    const calculateCommandStates = commandsToCheck => {
        let commandStates = mainControllerCommands.getCommandsStates({engineAdapter, propsCollector, commandsToCheck})
        const lastCommandsStates = privates.lastCommandStates

        commandStates = _.assign({}, lastCommandsStates, commandStates)
        if (!_.isEqual(lastCommandsStates, commandStates)) {
            if (lastCommandsStates.formatBlock !== commandStates.formatBlock) {
                toggleKeyStrokesIfNeeded(propsCollector.getCachedThemes()[commandStates.formatBlock] || {}, engineAdapter)
            }

            privates.lastCommandStates = commandStates
            privates.fire('onCmdStateChange', commandStates)
        }
    }

    const generateSelectionChangeHandler = commandsToCheck => _.debounce(() => {
        calculateCommandStates(commandsToCheck)
    }, 1)

    const handleSelectionChanged = generateSelectionChangeHandler()

    const handlePaste = e => {
        fixLinks(e, (type, data) => {
            if (type === 'ExternalLink') {
                return propsCollector.convertExternalLinkToLinkRef(data)
            } else if (type === 'InternalLink') {
                return propsCollector.convertLinkRefToLinkRef(data)
            }
        })
    }

    _.assign(privates, {
        generateSelectionChangeHandler,
        handleSelectionChanged,
        handlePaste,
        calculateCommandStates
    })
}

class MainController {
    constructor(engineAdapter, propsCollector, initialData, config = {shouldInjectFontsToMainDocument: false, setSelectionOnStart: true, validateHtml: true}) {
        const privates = {engineAdapter, propsCollector, ...config, lastCommandStates: {}}
        initMainControllerPrivateMethods.call(this, privates)
        mainControllerEvents.init.call(this, privates)
        initExposeEngineAdapterFunctions.call(this, privates.engineAdapter)
        mainControllerCommands.init.call(this, privates)
        initMainControllerPublicMethods.call(this, privates)

        this.setData(initialData, config)
        this.resetUndo()
    }

    /**
     * Creates the richtext instance
     * @param {Object} domElement - a DOM node to host the richtext instance
     * @param {string} [initialData=''] - Initial HTML data
     * @param {function} [onLoaded] - Callback function to be called when the instance is loaded, the manager instance will be passed as a parameter
     * @param {Object} [linksManager] - used to transform links, implements LinksManager interface
     * @param {Object} [stylesManager] - used to receive styles data (fonts, css, themes), implements StylesManager interface
     * @param {Object} [documentServicesApi] - if passed, it will create a default stylesManager based on the documentServicesApi
     * @param {number} [waitBeforeShowRichText] - time in milliseconds to wait before showing the richtext editor
     * @param {boolean} [shouldInjectFontsToMainDocument] - if true, the fonts css provided in styles manager will be injected to the parent document aswell
     * @param {boolean} [autoGrow] - if true, the editor will auto grow according to the content
     * @param {boolean} [autoGrowOnStartup] - if true, the editor will adjust its height on startup according to the initial content
     * @param {number} [autoGrowMinHeight] - set the minimum height the editor can shrink to (default is 15)
     * @param {number} [autoGrowMaxHeight] - set the maximum height the editor can grow to (default is null)
     * @param {boolean} [setSelectionOnStart] - if true, the content will be selected on start
     */
    static createInstance({domElement, initialData, onLoaded, linksManager = {}, stylesManager = {}, documentServicesApi, waitBeforeShowRichText = 200, shouldInjectFontsToMainDocument = false, autoGrow = true, autoGrowOnStartup = false, autoGrowMinHeight = 15, autoGrowMaxHeight = null, setSelectionOnStart = true, validateHtml = true}) {
        const _propsCollector = propsCollectorUtils.buildPropsCollector(documentServicesApi, linksManager, stylesManager)

        const onTextEngineReady = textEngineAdapter => {
            const config = {shouldInjectFontsToMainDocument, setSelectionOnStart, validateHtml}
            const mainController = new MainController(textEngineAdapter, _propsCollector, initialData, config)
            _.defer(() => {
                onLoaded(mainController)
                mainController.refreshPropsCollector()
                //avoid editor blinking on initialization
                setTimeout(() => {
                    textEngineAdapter.showEditor()
                    if (autoGrow && autoGrowOnStartup) {
                        mainController.commands.autogrow()
                    }
                }, waitBeforeShowRichText)
            })
        }

        TextEngineAdapter.createInstance({
            domElement,
            onInstanceReadyCallBack: onTextEngineReady,
            waitBeforeShowRichText,
            autoGrow,
            autoGrowOnStartup,
            autoGrowMinHeight,
            autoGrowMaxHeight
        })
    }
}

const exportVal = {
    createInstance: MainController.createInstance,
    createOfflineCommandInstance: TextEngineAdapter.createOfflineInstance
}

module.exports = exportVal



// WEBPACK FOOTER //
// ./src/mainController/mainController.js